import { useMemo } from 'react';
import { Link } from '@remix-run/react';
import type { Professional } from 'generated/graphql';
import { useConversation } from '../../context';
import type { Conversation, Participant } from '../../types';

/**
 * Banner shown when a chat box is opened from the professional side of an enquiry.
 * Returns `null` when the chat is being viewed as a regular personal inbox conversation.
 * @see {@link ChatBox}
 */
export function ChatBoxProfessionalBanner() {
    const { professionalID, conversation, selfParticipant } = useConversation();
    const bannerDetails = useMemo(() => {
        return getBannerDetails({
            conversation,
            professionalID,
            selfParticipant,
        });
    }, [conversation, professionalID, selfParticipant]);

    if (!bannerDetails) {
        return null;
    }

    const {
        professionalID: bannerProfessionalID,
        professional,
        message,
    } = bannerDetails;

    return (
        <div className="flex items-center gap-[8px] rounded-[8px] bg-gray-100 px-[12px] py-[8px] text-[13px] leading-normal text-black/80">
            {professional?.CompanyLogo && (
                <img
                    src={professional.CompanyLogo}
                    alt={professional.Title}
                    className="size-[28px] shrink-0 rounded-full object-contain"
                    style={{
                        backgroundColor:
                            professional.LogoBackgroundColor ?? 'white',
                    }}
                />
            )}
            <div className="min-w-0 flex-1">
                <span>{message} </span>
                {professional ? (
                    <Link
                        to={professional.Link}
                        className="font-medium text-black hover:underline"
                    >
                        {professional.Title}
                    </Link>
                ) : (
                    <span className="font-medium text-black">{`Professional #${bannerProfessionalID}`}</span>
                )}
            </div>
        </div>
    );
}

interface BannerDetails {
    professionalID: number;
    professional: Pick<
        Professional,
        'Title' | 'Link' | 'CompanyLogo' | 'LogoBackgroundColor'
    > | null;
    message: string;
}

/**
 * Get the professional details and message to display in the banner. This only
 * returns a value when the user is on the professional side of an enquiry conversation.
 */
function getBannerDetails({
    conversation,
    professionalID,
    selfParticipant,
}: {
    conversation: Conversation | undefined;
    professionalID: number | null;
    selfParticipant: Participant | null;
}): BannerDetails | null {
    if (professionalID && professionalID > 0) {
        // Company inbox: get professional participant.
        const participant = conversation?.Participants.find(
            ({ Identity }) =>
                Identity.__typename === 'Professional' &&
                Identity.ID === professionalID,
        );
        return {
            professionalID,
            professional:
                participant?.Identity.__typename === 'Professional'
                    ? participant.Identity
                    : null,
            message: 'Messaging on behalf of',
        };
    } else if (
        conversation?.__typename === 'EnquiryConversation' &&
        selfParticipant?.__typename === 'EnquiryParticipant' &&
        selfParticipant.IsCollaborator
    ) {
        // Collaborator: get enquiry professional.
        return {
            professionalID: conversation.InitialEnquiry.Professional.ID,
            professional: conversation.InitialEnquiry.Professional,
            message: 'You were invited to this chat by',
        };
    }
    return null;
}
